import { PaperConfig, PaperCode, SectionConfig, ReadingInsertConfig, QuestionCategory } from './types';
import { englishLanguagePaper1Config } from './englishLanguagePaper1';
import { englishLanguagePaper2Config } from './englishLanguagePaper2';
import { englishLiteraturePaper1Config } from './englishLiteraturePaper1';
import { englishLiteraturePaper2Config } from './englishLiteraturePaper2';

// Re-export types
export type { PaperConfig, PaperCode, SectionConfig, ReadingInsertConfig, QuestionCategory };

/**
 * All paper configurations indexed by paper code.
 */
export const paperConfigs: Record<PaperCode, PaperConfig> = {
  '1EN0/01': englishLanguagePaper1Config,
  '1EN0/02': englishLanguagePaper2Config,
  '1ET0/01': englishLiteraturePaper1Config,
  '1ET0/02': englishLiteraturePaper2Config,
};

/**
 * Get paper configuration by paper code.
 * Returns undefined if the paper code is not recognized.
 */
export function getPaperConfig(paperCode: string | null | undefined): PaperConfig | undefined {
  if (!paperCode) return undefined;
  return paperConfigs[paperCode as PaperCode];
}

/**
 * Get the section configuration for a given question category within a paper.
 */
export function getSectionForCategory(
  paperConfig: PaperConfig,
  category: QuestionCategory
): SectionConfig | undefined {
  return paperConfig.sections.find((section) => section.categories.includes(category));
}

/**
 * Check if a paper has a reading insert.
 */
export function hasReadingInsert(paperConfig: PaperConfig): boolean {
  return !!paperConfig.readingInsert;
}

/**
 * Check if a category requires dual reading insert view (for synthesis questions like Q7A/Q7B).
 */
export function requiresDualReadingView(
  paperConfig: PaperConfig,
  category: QuestionCategory
): boolean {
  return !!paperConfig.readingInsert?.dualViewCategories?.includes(category);
}

/**
 * Check if a section has question choice (like Section B where students pick one question).
 */
export function sectionHasQuestionChoice(section: SectionConfig): boolean {
  return !!section.hasQuestionChoice;
}

/**
 * Get all question categories that belong to Section A (non-choice questions).
 */
export function getSectionACategories(paperConfig: PaperConfig): QuestionCategory[] {
  const sectionA = paperConfig.sections.find((s) => s.id === 'A');
  return sectionA?.categories || [];
}

/**
 * Get all question categories that belong to sections with question choice.
 */
export function getChoiceSectionCategories(paperConfig: PaperConfig): QuestionCategory[] {
  return paperConfig.sections
    .filter((s) => s.hasQuestionChoice)
    .flatMap((s) => s.categories);
}
